import React from 'react'
import { ChevronRightIcon } from '@mote/assets/icons'
import { isNumber, useBoolean, useInterval } from '@mote/helpers'
import { noop } from '@mote/helpers/constants'
import { mergeClassnames } from '@mote/helpers/dom'
import styled from '@xstyled/styled-components'
import { Button } from '../button/button'
import { useStackedCarousel } from './stackedCarousel.context'

const CARDS_STATES = {
  INACTIVE: 'inactive',
  ACTIVE: 'active',
  NEXT: 'next',
  PREV: 'prev'
}

function getCardState(indexes, cardIndex) {
  if (indexes.currentIndex === cardIndex) {
    return CARDS_STATES.ACTIVE
  } else if (indexes.nextIndex === cardIndex) {
    return CARDS_STATES.NEXT
  } else if (indexes.previousIndex === cardIndex) {
    return CARDS_STATES.PREV
  }

  return CARDS_STATES.INACTIVE
}

function useAutoRotate(callback = noop, interval = 1000) {
  const [running, intervalActions] = useBoolean(false)

  useInterval(callback, running ? interval : null)

  return {
    startAutoRotate: intervalActions.setTrue,
    stopAutoRotate: intervalActions.setFalse,
    isAutoRotating: running
  }
}

const ControlButton = ({ action, ...props }) => {
  const iconProps =
    action === 'prev' ? { style: { transform: 'rotate(180deg)' } } : {}

  return (
    <ControlButtonRoot
      icon={<ChevronRightIcon color='currentColor' {...iconProps} />}
      variant='soft'
      {...props}
      aria-label={action === 'prev' ? 'Previous' : 'Next'}
      data-action={action}
    />
  )
}

export const StackedCarousel = ({
  className,
  carouselClassName,
  cardClassName,
  showButtons = true,
  leftButton: leftButtonComp,
  rightButton: rightButtonComp,
  autoRotate = false,
  rotationInterval = 2000,
  children,
  width = 250,
  height = 200,
  isCycling: isCyclingProp = true,
  cardSpacing = 40,
  ...props
}) => {
  const {
    goToNext,
    goToPrevious,
    canGoToNext,
    canGoToPrevious,
    indexes,
    isCycling
  } = useStackedCarousel({
    totalCards: children.length,
    isCycling: isCyclingProp
  })
  const { startAutoRotate, stopAutoRotate } = useAutoRotate(
    goToNext,
    rotationInterval
  )

  React.useEffect(() => {
    if (!autoRotate || !isCycling) return

    startAutoRotate()

    return () => stopAutoRotate()
  }, [])

  return (
    <Root
      className={mergeClassnames('mote-stacked-carousel-container', className)}
    >
      {showButtons &&
        canGoToPrevious() &&
        (leftButtonComp || (
          <ControlButton action='prev' onClick={goToPrevious} />
        ))}
      <CardsContainer
        role='list'
        className={mergeClassnames('mote-stacked-carousel', carouselClassName)}
        cardWidth={width}
        cardHeight={height}
        cardSpacing={cardSpacing}
        {...props}
      >
        {children.map((card, index) => {
          return (
            <Card
              key={index}
              role='listitem'
              className={mergeClassnames(
                'mote-stacked-carousel__card',
                cardClassName
              )}
              data-card-state={getCardState(indexes, index)}
            >
              {card}
            </Card>
          )
        })}
      </CardsContainer>
      {showButtons &&
        canGoToNext() &&
        (rightButtonComp || <ControlButton action='next' onClick={goToNext} />)}
    </Root>
  )
}

const Root = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: auto;
  padding: 0;
`

const ControlButtonRoot = styled(Button)`
  border-radius: 100%;
  z-index: 50;
`

const CardsContainer = styled.ul`
  --card-spacing: ${({ cardSpacing }) => cardSpacing}px;

  list-style: none !important;
  padding: 0 !important;
  position: relative;
  width: ${({ cardWidth, cardSpacing }) =>
    isNumber(cardWidth) ? `${cardWidth - cardSpacing}px` : cardWidth};
  height: ${({ cardHeight, cardSpacing }) =>
    isNumber(cardHeight) ? `${cardHeight - cardSpacing}px` : cardHeight};

  margin: 0 ${({ cardSpacing }) => cardSpacing / 2}px;
`

const Card = styled.li`
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: all 250ms ease-in-out;
  transform: scale(1) translateX(var(--card-spacing));

  & img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    overflow: hidden;
  }

  &[data-card-state='${CARDS_STATES.ACTIVE}'] {
    opacity: 1;
    z-index: 3;
    transform: scale(1) translateY(0);
  }

  &[data-card-state='${CARDS_STATES.NEXT}'] {
    opacity: 0.6;
    z-index: 2;
    transform: scale(0.9) translateX(calc(1.2 * var(--card-spacing)));
  }

  &[data-card-state='${CARDS_STATES.PREV}'] {
    z-index: 1;
    opacity: 0.6;
    transform: scale(0.9) translateX(calc(-1.2 * var(--card-spacing)));
  }

  &[data-card-state='${CARDS_STATES.INACTIVE}'] {
    z-index: 1;
    opacity: 0;
    transform: scale(1) translateX(0px);
  }
`
